import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getCookie, extractEmailFromUserIdCookie } from '@/src/utils/browser'
import { RootState } from '../rootReducer'

export interface UserProfile {
    firstName?: string
    lastName?: string
    fullName?: string
    picture?: string
    googleId?: string
}

export interface AuthState extends UserProfile {
    signedIn: boolean
    email?: string
    userId?: string | number
}

/**
 * Safely parses user data from cookies
 */
const parseUserData = (userData: string | null): Partial<UserProfile> => {
    if (!userData) return {}

    try {
        return JSON.parse(decodeURIComponent(userData))
    } catch (error) {
        console.error('Error parsing user data from cookie:', error)
        return {}
    }
}

/**
 * Extracts user ID from the userid cookie
 */
const extractUserId = (userIdCookie: string | null): string | undefined => {
    if (!userIdCookie) return undefined

    const idMatch = userIdCookie.match(/\|(\d+)\|/)
    return idMatch?.[1]
}

/**
 * Gets the initial auth state from cookies (client-side only)
 */
const getAuthStateFromCookies = (): AuthState => {
    const usermisc = getCookie('usermisc')
    const userid = getCookie('userid')
    const userData = getCookie('userData')

    // If not signed in, return default state
    if (!usermisc || !usermisc.includes('SIGNED_IN')) {
        return { signedIn: false }
    }

    // Parse user data from cookies
    const userInfo = parseUserData(userData || null)
    const email = userid ? extractEmailFromUserIdCookie(userid) : undefined
    const userId = userid ? extractUserId(userid) : (userInfo as { userId?: string | number })?.userId

    return {
        signedIn: true,
        email,
        userId,
        ...userInfo
    }
}

// Server-side safe initial state (always falsy to prevent hydration mismatch)
const initialState: AuthState = { signedIn: false }

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        /**
         * Updates the auth state with new user data
         */
        setAuthUser: (state, action: PayloadAction<Partial<AuthState>>) => {
            return { ...state, ...action.payload }
        },

        /**
         * Clears the auth state (on sign out)
         */
        clearAuthUser: () => ({
            signedIn: false,
            email: undefined,
            userId: undefined,
            firstName: undefined,
            lastName: undefined,
            fullName: undefined,
            picture: undefined,
            googleId: undefined
        }),

        /**
         */
        refreshAuthUser: () => getAuthStateFromCookies()
    }
})

// Action creators
export const { setAuthUser, clearAuthUser, refreshAuthUser } = authSlice.actions

// Selectors
export const selectAuth = (state: RootState) => state.auth
export const selectIsAuthenticated = (state: RootState) => state.auth.signedIn
export const selectCurrentUser = (state: RootState) => ({
    email: state.auth.email,
    userId: state.auth.userId,
    fullName: state.auth.fullName,
    firstName: state.auth.firstName,
    lastName: state.auth.lastName,
    picture: state.auth.picture
})

export default authSlice.reducer
